import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {AuthCheck} from '../../helpers'
import {
  SearchPaymentRequestClient,
  CreatePaymentRequestClient,
  GetPaymentRequestClient,
  DeletePaymentRequestClient,
  UpdatePaymentRequestClient,
  UpdateOnConfirmationStateClient,
  GetConfirmationStateClient,
  UpdatePaymentRequestConfirmationClient,
  GetWaitingConfirmationClient,
} from '../../api'

const SERVICE_URL = window.ENVVARS.REACT_APP_SERVICE_URL

// API Clients
const searchPaymentRequestClient = new SearchPaymentRequestClient(SERVICE_URL)
const createPaymentRequestClient = new CreatePaymentRequestClient(SERVICE_URL)
const getPaymentRequestClient = new GetPaymentRequestClient(SERVICE_URL)
const deletePaymentRequestClient = new DeletePaymentRequestClient(SERVICE_URL)
const updatePaymentRequestClient = new UpdatePaymentRequestClient(SERVICE_URL)
const updateOnConfirmationStateClient = new UpdateOnConfirmationStateClient(SERVICE_URL)
const getConfirmationStateClient = new GetConfirmationStateClient(SERVICE_URL)
const updatePaymentRequestConfirmationClient = new UpdatePaymentRequestConfirmationClient(
  SERVICE_URL
)
const getWaitingConfirmationClient = new GetWaitingConfirmationClient(SERVICE_URL)

const initialState = {
  loading: false,
  isRedirect: false,
  error: null,
  isDeleted: false,
  updateConfirmation: false,
  onConfirmationState: false,
  paymentRequest: null,
  detail: null,
  getConfirmationState: null,
  waitingConfirmation: null,
}

// Async Thunks
export const paymentRequestGetAll = createAsyncThunk(
  'paymentRequest/getAll',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchPaymentRequestClient.search(
        params.name,
        params.projectId,
        params.companyId,
        params.beginDate,
        params.endDate,
        params.requestedUserId,
        params.paymentRequestTypeId,
        params.paymentRequestStateId,
        params.items_Per_Page,
        params.page,
        params.order,
        params.sort
      )
      dispatch(setPaymentRequest(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const paymentRequestCreate = createAsyncThunk(
  'paymentRequest/create',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await createPaymentRequestClient.create(params)
      dispatch(setRedirect(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const paymentRequestGetId = createAsyncThunk(
  'paymentRequest/getId',
  async (id, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await getPaymentRequestClient.get(id)
      dispatch(setDetail(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const paymentRequestDelete = createAsyncThunk(
  'paymentRequest/delete',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await deletePaymentRequestClient.delete(params)
      dispatch(setDeleted(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const paymentRequestUpdate = createAsyncThunk(
  'paymentRequest/update',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await updatePaymentRequestClient.update(params)
      dispatch(setRedirect(response || params))
      dispatch(setDetail(params))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setDetail(params))
      dispatch(setError(error))
      throw error
    }
  }
)

export const paymentRequestUpdateOnConfirmationState = createAsyncThunk(
  'paymentRequest/updateOnConfirmationState',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await updateOnConfirmationStateClient.update(params)
      dispatch(setOnConfirmationState(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const paymentRequestGetConfirmationState = createAsyncThunk(
  'paymentRequest/getConfirmationState',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await getConfirmationStateClient.get(params)
      dispatch(setGetConfirmationState(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const paymentRequestUpdateConfirmation = createAsyncThunk(
  'paymentRequest/updateConfirmation',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await updatePaymentRequestConfirmationClient.update(params)
      dispatch(setUpdateConfirmation(true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const getWaitingConfirmationGetAll = createAsyncThunk(
  'paymentRequest/getWaitingConfirmationGetAll',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await getWaitingConfirmationClient.search(
        params.projectId,
        params.companyId,
        params.approveUserId,
        params.items_Per_Page,
        params.page,
        params.order,
        params.sort
      )
      dispatch(setWaitingConfirmation(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

// Slice
const paymentRequestSlice = createSlice({
  name: 'paymentRequest',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.loading = false
    },
    setPaymentRequest: (state, action) => {
      state.paymentRequest = action.payload
      state.loading = false
    },
    setDeleted: (state, action) => {
      state.isDeleted = action.payload
      state.loading = false
    },
    setRedirectPage: (state, action) => {
      state.isRedirectPage = action.payload
      state.loading = false
    },
    setRedirect: (state, action) => {
      state.isRedirect = action.payload
      state.loading = false
    },
    setDetail: (state, action) => {
      state.detail = action.payload
      state.loading = false
    },
    setOnConfirmationState: (state, action) => {
      state.onConfirmationState = action.payload
      state.loading = false
    },
    setGetConfirmationState: (state, action) => {
      state.getConfirmationState = action.payload
      state.loading = false
    },
    setUpdateConfirmation: (state, action) => {
      state.updateConfirmation = action.payload
      state.loading = false
    },
    setWaitingConfirmation: (state, action) => {
      state.waitingConfirmation = action.payload
      state.loading = false
    },
    resetState: (state) => {
      Object.assign(state, initialState)
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('paymentRequest/') && action.type.endsWith('/pending'),
      (state) => {
        state.loading = true
      }
    )
  }
})

// Export actions
export const {
  setLoading,
  setError,
  setPaymentRequest,
  setDeleted,
  setRedirectPage,
  setRedirect,
  setDetail,
  setOnConfirmationState,
  setGetConfirmationState,
  setUpdateConfirmation,
  setWaitingConfirmation,
  resetState,
} = paymentRequestSlice.actions

// Selectors
export const selectPaymentRequestLoading = (state) => state.paymentRequest.loading
export const selectPaymentRequestError = (state) => state.paymentRequest.error
export const selectPaymentRequest = (state) => state.paymentRequest.paymentRequest
export const selectPaymentRequestDeleted = (state) => state.paymentRequest.isDeleted
export const selectPaymentRequestRedirectPage = (state) => state.paymentRequest.isRedirectPage
export const selectPaymentRequestRedirect = (state) => state.paymentRequest.isRedirect
export const selectPaymentRequestDetail = (state) => state.paymentRequest.detail
export const selectPaymentRequestOnConfirmationState = (state) =>
  state.paymentRequest.onConfirmationState
export const selectPaymentRequestGetConfirmationState = (state) =>
  state.paymentRequest.getConfirmationState
export const selectPaymentRequestUpdateConfirmation = (state) =>
  state.paymentRequest.updateConfirmation
export const selectPaymentRequestWaitingConfirmation = (state) =>
  state.paymentRequest.waitingConfirmation

export default paymentRequestSlice.reducer
